import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Entities from '@shared/entities';
import { catchError, map } from 'rxjs/operators';
import { Observable, lastValueFrom, of } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { Result, ListResponse } from '@shared/models';
import { ConfigService } from '../services/config.service';

@Injectable({
  providedIn: 'root',
})
export class ManufacturersApi {
  private serviceUri = '';

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private config: ConfigService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/manufacturers/`;
      }
    });
  }

  async loadManufacturers(_force: boolean = false): Promise<ListResponse<Entities.Manufacturer>> {
    const result = await lastValueFrom(this.http.get<Result<ListResponse<Entities.Manufacturer>>>(this.serviceUri));
    if (result.successful) {
      return result.data;
    }

    this.log.displayError(result.message || 'Unable to load manufacturers', 'Manufacturers');
    return { records: [] } as ListResponse<Entities.Manufacturer>;
  }

  async create(manufacturer: { name: string }): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.post<Result<number>>(this.serviceUri, manufacturer));
    if (!result.successful) {
      console.debug('create', result);
    }
    return result;
  }

  async update(id: number, manufacturer: { name: string }): Promise<Result<void>> {
    const result = await lastValueFrom(this.http.put<Result<void>>(this.serviceUri + id, manufacturer));
    if (result.successful) {
      this.log.displaySuccess('Manufacturer updated', 'Manufacturers');
    }
    return result;
  }

  async delete(id: number): Promise<Result<void>> {
    const result = await lastValueFrom(this.http.delete<Result<void>>(this.serviceUri + id));
    if (!result.successful) {
      console.debug('delete', result);
    }
    return result;
  }

  getManufacturers(): Observable<Entities.Manufacturer[]> {
    return this.http.get<Result<ListResponse<Entities.Manufacturer>>>(this.serviceUri).pipe(
      map((response) => response.data.records),
      catchError(this.handleError<Entities.Manufacturer[]>('getManufacturers', []))
    );
  }

  getManufacturer(id: number): Observable<Entities.Manufacturer | undefined> {
    return this.http.get<Result<Entities.Manufacturer>>(this.serviceUri + id).pipe(
      map((response) => response.data),
      catchError(this.handleError<Entities.Manufacturer | undefined>('getManufacturer', undefined))
    );
  }

  private handleError<T>(_operation = 'operation', result?: T): (error: unknown) => Observable<T> {
    return (error: unknown): Observable<T> => {
      this.log.logError(error);
      return of(result as T);
    };
  }
}
